import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TeamSectionItemType } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormErrorComponent from 'webapp/components/form-error';
import FormSelectComponent from 'webapp/components/form/select';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Team, TeamSection } from 'webapp/models-custom';

export default class TeamPopupAddItemComponent extends PopupInnerModalComponent<PopupData.Teams.AddItem> {
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;

	@tracked selectedTeamId: string | null = null;
	@tracked selectedTeamSectionId: string | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void this.mmTeamsService.requestTeams();
	}

	@cached
	get teams(): LabelValuePairString[] {
		return this.mmTeamsService.userTeamModels.map((team) => ({
			value: team.id!,
			label: team.name,
		}));
	}

	get selectedTeam(): Team | null {
		return (
			this.mmTeamsService.teamModels.find((team) => team.id === this.selectedTeamId) ?? null
		);
	}

	get teamSections(): LabelValuePairString[] {
		return (
			this.selectedTeam?.orderedSections.map((section) => ({
				value: section.id!,
				label: section.name,
			})) ?? []
		);
	}

	get selectedTeamSection(): TeamSection | null {
		return (
			this.selectedTeam?.orderedSections.find(
				(section) => section.id === this.selectedTeamSectionId,
			) ?? null
		);
	}

	get isSaveDisabled(): boolean {
		return (
			!this.selectedTeamId || !this.selectedTeamSectionId || this.teamSectionError.length > 0
		);
	}

	get isTeamSectionSelectDisabled(): boolean {
		return !this.selectedTeamId || this.submitInProgress;
	}

	get itemCount(): number {
		return this.popupData.metricSetupData?.length ?? 1;
	}

	get teamSectionError() {
		const section = this.selectedTeamSection;

		if (section?.isFull) {
			return [{ message: 'Space section is full.', name: 'general' }];
		}
		if (section && !section.hasRoomFor(this.itemCount)) {
			return [
				{
					message: `Space section has no room for ${this.itemCount} more items.`,
					name: 'general',
				},
			];
		}
		return [];
	}

	@action
	onTeamChange(teamId: string) {
		if (this.selectedTeamId !== teamId) {
			this.selectedTeamId = teamId;
			this.submitInProgress = true;
			void this.mmTeamsService.requestSections(teamId).finally(() => {
				this.submitInProgress = false;
			});
		}
	}

	@action
	onTeamSectionChange(teamSectionId: string) {
		this.selectedTeamSectionId = teamSectionId;
	}

	@action
	save() {
		const { objectType, objectId, metricSetupData, onItemAdded } = this.popupData,
			selectedTeamSection = this.selectedTeamSection;

		const teamName = this.selectedTeam?.name ?? '';
		const teamId = this.selectedTeam?.id ?? '';

		if (objectType === TeamSectionItemType.METRIC && metricSetupData && selectedTeamSection) {
			this.submitInProgress = true;

			void Promise.all(
				metricSetupData.map((data) =>
					this.mmTeamSectionItemsService.preparePayloadForCreate(data),
				),
			)
				.then((payloads) =>
					this.mmTeamSectionsService.batchAddItems(
						selectedTeamSection,
						payloads.map((objectData) => ({ objectType, objectData })),
					),
				)
				.then(() => {
					onItemAdded?.(teamName, teamId);
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				});
		} else {
			void selectedTeamSection
				?.addItem(objectType, objectId !== null ? Number(objectId) : undefined)
				.then(() => {
					onItemAdded?.(teamName, teamId);
					this.close();
				});
		}
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Spaces" @size="lg" />
			Add item to Space
		</div>

		<div class="content">
			<div class="row input">
				<label>Space</label>
				<div class="right">
					<FormSelectComponent
						name="ownerId"
						@selected={{this.selectedTeamId}}
						@options={{this.teams}}
						@onChange={{this.onTeamChange}}
						@placeholder="Select space"
						@showFloatlabel={{false}}
						@isDisabled={{this.submitInProgress}}
						@isRequired={{true}}
						@isNumeric={{false}}
					/>
				</div>
			</div>

			<div class="row input">
				<label>Space section</label>
				<div class="right">
					<FormSelectComponent
						name="ownerId"
						@selected={{this.selectedTeamSectionId}}
						@options={{this.teamSections}}
						@onChange={{this.onTeamSectionChange}}
						@placeholder="Select space section"
						@showFloatlabel={{false}}
						@isDisabled={{this.isTeamSectionSelectDisabled}}
						@isRequired={{true}}
						@isNumeric={{false}}
					/>
					<RowErrorComponent @items={{this.teamSectionError}} />
					<FormErrorComponent @errors={{this.teamSectionError}} />
				</div>
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Add to Space"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>

		</div>
	</template>
}
